import { type FieldReadDatabaseSnapshot } from '@databases/field-read-modules';
import { type BacklinkReadSnapshot } from '@databases/field-read-modules/backlink';
import { type EntryLinkDatabaseResourceInput } from '@databases/field-read-modules/entry-link';
import { type DefaultStore } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { getSelectDatabaseDraft } from '@databases/redux/selectors/getSelectDatabaseDraft';
import { DatabaseFieldType } from '@databases/types/database';
import { getErrStatus } from '@databases/utils/confluence/fetch';
import { getBacklinkEntries } from '@databases/utils/database/field-types/backlink';
import {
  createField,
  getDatabaseEntryOptional,
  getEntryName
} from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByIdOptional
} from '@databases/utils/redux/databases/selectors';
import { getEntryPath } from '@databases/utils/urls';
import pLimit from 'p-limit';

type BacklinkStore = Pick<DefaultStore, 'dispatch' | 'getState'>;
const selectDraft = getSelectDatabaseDraft(true);
/**
 * Captures source identity at application composition and reuses the V1 reverse
 * index and draft visibility. Count reads do not resolve linked entry names.
 * The cache follows related databases, not visited cell addresses; replacing the
 * application binding releases it. This initial read source does not subscribe.
 */
export function createBacklinkReadSource({
  store,
  baseUrl,
  contentId,
  getSourceContentId
}: {
  readonly store: BacklinkStore;
  readonly baseUrl: string;
  readonly contentId: string;
  readonly getSourceContentId?: (snapshot: FieldReadDatabaseSnapshot) => string;
}) {
  const limit = pLimit(8);
  const snapshots = new Map<
    string,
    { state: ReturnType<BacklinkStore['getState']>; value: BacklinkReadSnapshot }
  >();
  const key = (input: EntryLinkDatabaseResourceInput) =>
    JSON.stringify([input.contentId, input.databaseId, input.databaseSpaceKey]);
  const snapshot = (input: EntryLinkDatabaseResourceInput): BacklinkReadSnapshot => {
    const state = store.getState();
    const cached = snapshots.get(key(input));
    if (cached?.state === state) {
      return cached.value;
    }
    const field = createField(DatabaseFieldType.BACKLINK, {
      settings: {
        ...createField(DatabaseFieldType.BACKLINK).settings,
        contentId: input.contentId,
        databaseId: input.databaseId,
        databaseSpace: input.databaseSpaceKey
          ? { key: input.databaseSpaceKey }
          : undefined
      }
    });
    const value: BacklinkReadSnapshot = Object.freeze({
      status: 'ready',
      read(
        sourceSnapshot: FieldReadDatabaseSnapshot,
        entryId: string,
        includeLinks: boolean
      ) {
        const database = selectDatabaseDraftByContentId(state, {
          contentId: getSourceContentId?.(sourceSnapshot) ?? contentId,
          includeDeleted: true
        });
        const entry = getDatabaseEntryOptional(database, entryId);
        const targets = getBacklinkEntries({ database, field, entry }, state, {
          selectDatabase: selectDraft
        });
        return Object.freeze({
          count: targets.length,
          links: Object.freeze(
            includeLinks
              ? targets.map((target) =>
                  Object.freeze({
                    entryId: target.entry.id,
                    title: getEntryName(target, state),
                    href: `${baseUrl}${getEntryPath({ contentId: target.database.contentId, entryId: target.entry.id, spaceKey: target.database.space.key })}`
                  })
                )
              : []
          )
        });
      }
    });
    snapshots.set(key(input), { state, value });
    return value;
  };
  return {
    getDatabaseSnapshot(input: EntryLinkDatabaseResourceInput) {
      const state = store.getState();
      const selected =
        input.databaseId && input.databaseSpaceKey
          ? selectDatabaseByIdOptional(state, {
              id: input.databaseId,
              spaceKey: input.databaseSpaceKey
            })
          : input.contentId
            ? selectDatabaseByContentIdOptional(state, { contentId: input.contentId })
            : undefined;
      return selected ? snapshot(input) : undefined;
    },
    getDatabaseResource: (input: EntryLinkDatabaseResourceInput) =>
      Object.freeze({
        key: `backlink:${key(input)}:${baseUrl}`,
        load: (signal: AbortSignal) =>
          limit(async () => {
            if (signal.aborted) {
              return undefined;
            }
            try {
              if (input.contentId) {
                await store.dispatch(loadDatabaseByContentId(input.contentId));
              }
            } catch (error: unknown) {
              return signal.aborted
                ? undefined
                : Object.freeze({
                    status:
                      getErrStatus(error) === 403
                        ? ('restricted' as const)
                        : ('broken' as const)
                  });
            }
            return signal.aborted ? undefined : snapshot(input);
          })
      })
  };
}
